import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Row,
  Col,
  Table,
  Button,
  Input,
  Label,
  Form,
  FormGroup,
} from "reactstrap";
import AuthService from "services/auth.service";

// Crypto-specific icons
const CRYPTO_ICONS = ["BTC", "ETH", "USDT", "USDC"];

// P2P-specific options
const P2P_OPTIONS = ["CASHAPP", "PAYPAL", "ZELLE", "CHIME"];

const emptyForm = { 
  name: "", 
  address: "", 
  network: "", 
  type: "", 
  icon: "", 
  enabled: true, 
  instructions: "", 
  requiresTag: false, 
  tagLabel: "" 
};

export default function Wallets() {
  const [wallets, setWallets] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: Type selection, 2: Form details

  const load = async () => {
    try {
      const res = await AuthService.getWallets();
      setWallets(res.data);
    } catch (e) {
      setWallets([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Prepare form data with P2P address and network handling
    const formData = { ...form };
    
    // For P2P wallets, always set address, network, and requiresTag for database consistency
    if (formData.type === "p2p") {
      formData.address = "p2p";
      formData.network = "p2p";
      formData.requiresTag = true; // Always require tag for P2P
    }
    
    console.log('Submitting form:', formData);
    
    try {
      let response;
      if (editingId) {
        response = await AuthService.updateWallet(editingId, formData);
      } else {
        response = await AuthService.createWallet(formData);
      }
      setForm(emptyForm);
      setEditingId(null);
      setCurrentStep(1); // Reset to step 1
      await load();
    } catch (error) {
      console.error('Error:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const onEdit = (w) => {
    console.log('Editing wallet:', w);
    setForm({ 
      name: w.name || "", 
      address: w.address || "", 
      network: w.network || "", 
      type: w.type || "crypto", 
      icon: w.icon || "", 
      enabled: w.enabled !== undefined ? w.enabled : true,
      instructions: w.instructions || "",
      requiresTag: w.requiresTag || false,
      tagLabel: w.tagLabel || ""
    });
    setEditingId(w.id);
    setCurrentStep(2); // Go directly to form step when editing
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this wallet?")) return;
    await AuthService.deleteWallet(id);
    await load();
  };

  const toggleEnabled = async (wallet) => {
    try {
      const updatedWallet = { ...wallet, enabled: !wallet.enabled };
      await AuthService.updateWallet(wallet.id, updatedWallet);
      await load();
    } catch (error) {
      console.error('Error toggling wallet:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  // Handle wallet type selection
  const handleTypeSelection = (type) => {
    const updatedForm = { ...form, type };
    
    // For P2P wallets, automatically set address, network, and requiresTag
    if (type === "p2p") {
      updatedForm.address = "p2p";
      updatedForm.network = "p2p";
      updatedForm.requiresTag = true; // Always require tag for P2P
      console.log('P2P wallet selected, address and network set to "p2p", requiresTag set to true:', updatedForm);
    }
    
    setForm(updatedForm);
    setCurrentStep(2);
  };

  // Reset form and go back to step 1
  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setCurrentStep(1);
  };

  // Go back to step 1
  const goBackToStep1 = () => {
    setCurrentStep(1);
    if (!editingId) {
      setForm({ ...form, type: "", address: "", network: "", requiresTag: false, tagLabel: "" });
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md="6">
          <Card>
            <CardHeader>
              <CardTitle tag="h5">All Wallets</CardTitle>
            </CardHeader>
            <CardBody>
              <Table responsive>
                <thead className="text-primary">
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Address/Tag</th>
                    <th>Network</th>
                    <th>Requires Tag</th>
                    <th>Enabled</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody style={{ color: "green", fontWeight: 400 }}>
                  {wallets.map((w) => (
                    <tr key={w.id}>
                      <td>{w.name}</td>
                      <td>
                        <span className={`badge ${
                          w.type === 'crypto' ? 'badge-primary' : 
                          w.type === 'p2p' ? 'badge-success' : 'badge-warning'
                        }`}>
                          {w.type}
                        </span>
                      </td>
                      <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {w.address}
                      </td>
                      <td>{w.network}</td>
                      <td>{w.requiresTag ? "Yes" : "No"}</td>
                      <td>
                        <Button 
                          size="sm" 
                          color={w.enabled ? "success" : "secondary"}
                          onClick={() => toggleEnabled(w)}
                        >
                          {w.enabled ? "Enabled" : "Disabled"}
                        </Button>
                      </td>
                      <td style={{ display: "flex", justifyContent: 'space-between' }}>
                        <Button size="sm" color="primary" onClick={() => onEdit(w)} style={{ marginRight: 1 }}>Edit</Button>
                        <Button size="sm" color="danger" onClick={() => onDelete(w.id)}>Delete</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
        <Col md="6">
          <Card>
            <CardHeader>
              <CardTitle tag="h5">
                {editingId ? "Edit Wallet" : "Add Wallet"}
                {!editingId && currentStep === 1 && " - Step 1: Select Type"}
                {!editingId && currentStep === 2 && " - Step 2: Configure Details"}
              </CardTitle>
            </CardHeader>
            <CardBody>
              {currentStep === 1 && !editingId ? (
                // Step 1: Wallet Type Selection
                <div>
                  <h6 style={{ marginBottom: '20px', color: '#6c757d' }}>
                    Choose the type of wallet you want to create:
                  </h6>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div 
                      style={{ 
                        padding: '20px', 
                        border: '2px solid #007bff', 
                        borderRadius: '10px', 
                        cursor: 'pointer',
                        backgroundColor: '#f8f9fa',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => handleTypeSelection('crypto')}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#e3f2fd'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    >
                      <h5 style={{ margin: '0 0 10px 0', color: '#007bff' }}>🔗 Cryptocurrency</h5>
                      <p style={{ margin: 0, color: '#6c757d' }}>
                        Create a cryptocurrency wallet (BTC, ETH, USDT, USDC)
                      </p>
                    </div>
                    
                    <div 
                      style={{ 
                        padding: '20px', 
                        border: '2px solid #28a745', 
                        borderRadius: '10px', 
                        cursor: 'pointer',
                        backgroundColor: '#f8f9fa',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => handleTypeSelection('p2p')}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#e8f5e8'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    >
                      <h5 style={{ margin: '0 0 10px 0', color: '#28a745' }}>💳 P2P Payment</h5>
                      <p style={{ margin: 0, color: '#6c757d' }}>
                        Create a P2P payment method (CashApp, PayPal, Zelle, Chime)
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                // Step 2: Wallet Configuration Form
                <Form onSubmit={onSubmit}>
                  {!editingId && (
                    <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#e9ecef', borderRadius: '5px' }}>
                      <small>
                        <strong>Creating:</strong> {form.type === 'crypto' ? '🔗 Cryptocurrency' : '💳 P2P Payment'} Wallet
                        <Button 
                          size="sm" 
                          color="link" 
                          style={{ padding: '0 5px', fontSize: '12px' }}
                          onClick={goBackToStep1}
                        >
                          (Change Type)
                        </Button>
                      </small>
                    </div>
                  )}

                  <FormGroup>
                    <Label>Name *</Label>
                    <Input 
                      value={form.name} 
                      onChange={e => setForm({ ...form, name: e.target.value })} 
                      required 
                      placeholder={form.type === "crypto" ? "e.g., Bitcoin, Ethereum" : "e.g., CashApp, PayPal"}
                    />
                  </FormGroup>

                  {form.type === "crypto" ? (
                    // Crypto-specific fields
                    <>
                      <FormGroup>
                        <Label>Wallet Address *</Label>
                        <Input 
                          value={form.address} 
                          onChange={e => setForm({ ...form, address: e.target.value })} 
                          required 
                          placeholder="Enter the cryptocurrency wallet address"
                        />
                      </FormGroup>
                      
                      <FormGroup>
                        <Label>Network</Label>
                        <Input 
                          value={form.network} 
                          onChange={e => setForm({ ...form, network: e.target.value })} 
                          placeholder="e.g., ERC20, TRC20, BEP20"
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>Icon *</Label>
                        <Input 
                          type="select" 
                          value={form.icon} 
                          onChange={e => setForm({ ...form, icon: e.target.value })}
                          required
                        >
                          <option value="">Select a cryptocurrency</option>
                          {CRYPTO_ICONS.map(icon => (
                            <option key={icon} value={icon}>{icon}</option>
                          ))}
                        </Input>
                      </FormGroup>
                    </>
                  ) : (
                    // P2P-specific fields
                    <>
                      <FormGroup>
                        <Label>P2P Service *</Label>
                        <Input 
                          type="select" 
                          value={form.icon} 
                          onChange={e => setForm({ ...form, icon: e.target.value })}
                          required
                        >
                          <option value="">Select a P2P service</option>
                          {P2P_OPTIONS.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </Input>
                      </FormGroup>

                      {/* P2P Tag Label Input - Always shown for P2P */}
                      <FormGroup>
                        <Label>Tag Label *</Label>
                        <Input
                          value={form.tagLabel}
                          onChange={e => setForm({ ...form, tagLabel: e.target.value })}
                          placeholder="e.g., Cashtag, Email, Username"
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>Instructions</Label>
                        <Input
                          type="textarea"
                          value={form.instructions}
                          onChange={e => setForm({ ...form, instructions: e.target.value })}
                          placeholder="Send payment to this username. Include your username in the payment notes for verification..."
                          rows="3"
                        />
                      </FormGroup>
                    </>
                  )}
                  
                  {/* Enabled checkbox */}
                  <div style={{ 
                    marginBottom: '20px',
                    padding: '15px',
                    border: '2px solid #28a745',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa'
                  }}>
                    <label style={{ 
                      fontSize: '16px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      margin: 0
                    }}>
                      <input
                        type="checkbox"
                        checked={form.enabled}
                        onChange={e => setForm({ ...form, enabled: e.target.checked })}
                        style={{ 
                          width: '20px',
                          height: '20px',
                          marginRight: '10px',
                          cursor: 'pointer'
                        }}
                      />
                      Enabled (Visible to clients)
                    </label>
                  </div>
                  
                  <div style={{ marginTop: 20 }}>
                    <Button color="primary" disabled={loading} type="submit" style={{ marginRight: '10px' }}>
                      {loading ? 'Saving...' : (editingId ? 'Update Wallet' : 'Create Wallet')}
                    </Button>
                    <Button color="secondary" type="button" onClick={resetForm}>
                      {editingId ? 'Cancel' : 'Reset'}
                    </Button>
                  </div>
                </Form>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add CSS for badges */}
      <style jsx>{`
        .badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
        }
        .badge-primary { background-color: #007bff; color: white; }
        .badge-success { background-color: #28a745; color: white; }
        .badge-warning { background-color: #ffc107; color: #212529; }
      `}</style>
    </div>
  );
}